import {Trans} from '@lingui/macro';
import React, {useCallback, useMemo} from 'react';

import {styled} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useToggle} from '../../../../../../srcCommon/helpers/hookHelpers';
import {ClientRequiredTaggingParameter} from '../../../../../models/api/requiredTaggingModel';
import {SafetyNetModel} from '../../../../../models/api/safetyNetModel';
import {RuleAddCard} from '../../ruleAddCard';
import {RuleEditorSection} from '../../ruleEditorSection';
import {RequiredTaggingEditorActions} from './hooks/useRequiredTaggingEditorState';
import {RequiredTaggingEditorTagList} from './requiredTaggingEditorTagList';

/*
 * Props.
 */

interface RequiredTaggingEditorActionsParamsProps {
  actions: ReadonlyArray<ClientRequiredTaggingParameter>;
  safetyNets: ReadonlyArray<SafetyNetModel>;
  notDisplayingErrorCards?: ReadonlyArray<string>;
  isEditing: boolean;

  onAddAction: RequiredTaggingEditorActions['onAddActionParameter'];
  onUpdateAction: RequiredTaggingEditorActions['onUpdateActionParameter'];
  onDeleteAction: RequiredTaggingEditorActions['onDeleteActionParameter'];
  onValidation: RequiredTaggingEditorActions['onValidation'];
  onSetHiddenErrors: RequiredTaggingEditorActions['onSetHiddenErrors'];
}

/*
 * Styles.
 */

const StyledRuleAddCardWrapperDiv = styled.div`
  margin-top: 15px;
`;

/*
 * Component.
 */

export const RequiredTaggingEditorActionsParams: React.FC<RequiredTaggingEditorActionsParamsProps> = (
  props,
) => {
  const {
    actions,
    notDisplayingErrorCards,
    onAddAction,
    onUpdateAction,
    onDeleteAction,
    onValidation,
    onSetHiddenErrors,
    isEditing,
    safetyNets,
  } = props;

  const [shouldShowMoreOptions, toggleMoreOptions] = useToggle();
  const safetyNetNames = useMemo(() => new Set(safetyNets.map((net) => net.name)), [safetyNets]);

  // Tag List (Safety Net) names are unique per namespace.
  const isConflicting = useCallback(
    (requiredTaggingParam: ClientRequiredTaggingParameter) => {
      const name = requiredTaggingParam.tagListName.trim();
      if (safetyNetNames.has(name)) {
        return true;
      }

      const sameNameCount = actions.filter((action) => action.tagListName.trim() === name).length;
      return sameNameCount > 1;
    },
    [actions, safetyNetNames],
  );

  return (
    <RuleEditorSection
      description={<Trans id="rules-editor-section-header-then">Then…</Trans>}
      shouldShowMoreOptions={shouldShowMoreOptions}
      toggleMoreOptions={toggleMoreOptions}
    >
      {actions.map((requiredTaggingParam) => (
        <RequiredTaggingEditorTagList
          key={requiredTaggingParam.clientId}
          shouldHideError={notDisplayingErrorCards?.includes(requiredTaggingParam.clientId) ?? false}
          isEditing={isEditing}
          isConflicting={isConflicting(requiredTaggingParam)}
          tagListParam={requiredTaggingParam}
          onSetHiddenErrors={onSetHiddenErrors}
          onUpdateTagList={(updatedTagListParam) => {
            onUpdateAction(
              (tagListParam) => tagListParam.clientId === updatedTagListParam.clientId,
              () => updatedTagListParam,
            );
          }}
          onDeleteTagList={
            shouldShowMoreOptions
              ? () =>
                  onDeleteAction((tagListParam) => tagListParam.clientId === requiredTaggingParam.clientId)
              : undefined
          }
          onValidation={onValidation}
        />
      ))}
      {shouldShowMoreOptions ? (
        <StyledRuleAddCardWrapperDiv onClick={onAddAction}>
          <RuleAddCard label={<Trans id="required-tagging-editor-add-tag-list">Tag list</Trans>} />
        </StyledRuleAddCardWrapperDiv>
      ) : null}
    </RuleEditorSection>
  );
};
